import { getBackendUrl } from "@Utils";
import { estimateFilesUrl } from "@Services/dataManager";
import { useTranslation } from "@i18n";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { addFilesToEstimate, isEstimateRequiredPermission, removeFilesFromEstimate } from "@Actions/estimation";
import { FileManagerAsPopup } from "@Features/files";
import {
  ADDON_CODE_TO_LAYOUT_FLAG,
  ADDONS_DISPLAY_NAME,
  BIM_TAKEOFF_ADDON_CODE,
  errorCodes,
  getViewerUrl,
} from "@Helpers";
import { convertDate } from "@Services/luxonHelper";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import { useNavigate, useParams } from "react-router-dom";
import IconComponent from "@CubeComponents/icons";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import axiosWrapper from "@AxiosWrapper";

import { renderUserTag } from "@CubeComponents/html/userTagDom";
import { isFileVersionUpgradePossible } from "@Services/utils/helpers";
import SlickGrid1, { typedColumns } from "@CubeComponents/html/SlickGrid1";
import type React from "react";
import type { GridClickEvent, SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { folderPermissions } from "@Helpers/constants";
import _ from "lodash";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import WizardResults from "./TakeoffWizardResults";
import Text from "@CubeComponents/html/Text/Text";
import useLayoutModuleAccess from "@Services/useLayoutModuleAccess";
import type { RootState } from "src/reducers";
import type {
  EstimateData as EstimateDataState,
  GetEstimateFilesResponse,
  UpgradeFileVersionRequest,
  UpgradeFileVersionResponse,
} from "@ServerTypes/estimates/estimates.schema";
import { RESOURCE_TYPE_MERGED_FILE } from "@Features/transmittals/hooks/transmittal.types";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
const { RESOURCE_MODIFY } = folderPermissions();

// `id` is required for SlickGrid row identity; the schema marks it optional because
// other consumers (e.g. CRUD operations) reuse the same row shape with id absent.
// Index into the server row rather than re-declaring `id: string` — that intersection
// narrowed the field and dropped its `ObjectIdString` brand.
type EstimateFileRow = NonNullable<GetEstimateFilesResponse["result"]>[number];
type FileRecord = EstimateFileRow & { id: NonNullable<EstimateFileRow["id"]> };

type EstimateDataDetail = EstimateDataState & {
  space?: { _id?: string };
  userRole?: number;
};

type FilesProps = {
  estimateId?: ObjectIdString;
};

function Files({ estimateId }: FilesProps) {
  const { t } = useTranslation();
  const filesGrid = useRef<SlickGrid1Ref<FileRecord> | null>(null);
  // Route params are the external entry point for these ids; the backend route schema validates them.
  const params = useParams<{ estimateId: ObjectIdString; versionId: ObjectIdString; projectId: ObjectIdString }>();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const estimateData = useSelector((state: RootState) => state.estimateData) as EstimateDataDetail;

  const [showFileManager, setShowFileManager] = useState(false);
  const [filesQuery] = useState({
    estimateId,
    versionId: params.versionId,
    params: {
      estimateId,
      versionId: params.versionId,
    },
  });
  const [toolbarButtonDisableState, setToolbarButtonDisableState] = useState({
    add: true,
    remove: true,
  });

  const [upgradeFile, setUpgradeFile] = useState<FileRecord | null>(null);
  const [isUpgradeAndReRunWizard, setIsUpgradeAndReRunWizard] = useState(true);
  const [openResultsModal, setOpenResultsModal] = useState(false);
  const [errorInAnyRecord, setErrorInAnyRecord] = useState(false);
  const [isResultLoadingDone, setIsResultLoadingDone] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const addonsAccess = useLayoutModuleAccess(ADDON_CODE_TO_LAYOUT_FLAG[BIM_TAKEOFF_ADDON_CODE]);

  useEffect(() => {
    if (estimateData._id) {
      setToolbarButtonDisableState((prev) => ({
        ...prev,
        add: Boolean(params.versionId) || !isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id),
      }));
    }
  }, [estimateData, params.versionId]);

  const toolbarOptions = useMemo(
    () => [
      {
        id: "add_file",
        name: t("Add"),
        prefixIcon: "add",
        disabled: toolbarButtonDisableState.add,
      },
      {
        id: "remove_file",
        name: t("Remove"),
        prefixIcon: "delete",
        disabled: toolbarButtonDisableState.remove,
      },
    ],
    [toolbarButtonDisableState],
  );

  const toolbarClickHandler = useCallback(
    async (buttonClicked: string) => {
      if (buttonClicked === "add_file") {
        if (!params.versionId) {
          setShowFileManager(true);
          return;
        }

        dispatch(
          showToastNotification({
            content: t("File can not be added while in preview mode"),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return;
      }

      if (buttonClicked === "remove_file") {
        const grid = filesGrid.current;
        if (!grid) {
          return;
        }
        setToolbarButtonDisableState({ add: true, remove: true });
        // `slickGrid.getDataItem` is `any` on the vendor grid and hands back `undefined` for a
        // selected index that no longer maps to a row — a selection kept across a refresh or a
        // filter change — so the rows are read as possibly-missing and dropped before use.
        const selectedRows = grid.slickGrid
          .getSelectedRows()
          .map((row: number): FileRecord | undefined => grid.slickGrid.getDataItem(row))
          .filter((row): row is FileRecord => row !== undefined);
        if (selectedRows.length === 1 && !params.versionId) {
          const selectedRow = selectedRows[0];
          if (!selectedRow?.id || !estimateId) {
            return;
          }
          await removeFilesFromEstimate(selectedRow.id, estimateId);
          setToolbarButtonDisableState({ add: false, remove: true });
          filesGrid.current?.refresh?.();
          return;
        }

        if (params.versionId) {
          dispatch(
            showToastNotification({
              content: t("File can not be removed while in preview mode"),
              type: TOAST_TYPE_ERROR,
            }),
          );
          return;
        }

        dispatch(
          showToastNotification({
            content: t("Please select a file to remove"),
            type: TOAST_TYPE_WARNING,
          }),
        );

        setToolbarButtonDisableState({ add: false, remove: false });
      }
    },
    [params.versionId, dispatch, estimateId],
  );

  const selectionEvent = () => {
    const grid = filesGrid.current?.slickGrid;
    if (grid) {
      const totalRowsSelected = grid.getSelectedRows().length;

      if (totalRowsSelected === 1) {
        if (!params.versionId && isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id)) {
          setToolbarButtonDisableState((prev) => ({ ...prev, remove: false }));
        }
      } else {
        setToolbarButtonDisableState((prev) => ({ ...prev, remove: true }));
      }
    }
  };

  const recordClick = (args: { detail: { args: { cell: number; row: number } } }) => {
    if (args.detail.args.cell === 0) {
      return;
    }
    const grid = filesGrid.current?.slickGrid;
    if (!grid) {
      return;
    }
    const rowData: FileRecord = grid.getDataItem(args.detail.args.row);
    if (rowData.fileDeleted) {
      return;
    }
    if (params.projectId) {
      const viewerUrl = getViewerUrl({
        fileVersionId: rowData.id,
        estimateId,
        versionId: params.versionId,
        spaceId: rowData.spaceId,
        projectId: params.projectId,
      });
      if (viewerUrl) {
        navigate(viewerUrl);
      }
    }
  };

  /**
   * Payload from FileManagerAsPopup keyed by file id → selected version row.
   * FileManagerAsPopup declares this callback argument as `any`, so this local type is the single
   * declaration site for its shape; `fileVersion` is a file version _id (24-hex ObjectId).
   */
  type SelectedFilesPayload = { [fileId: string]: { fileVersion: ObjectIdString } | undefined };

  const filesAdded = async (listOfFiles: SelectedFilesPayload) => {
    setIsButtonLoading(true);
    if (!estimateId) {
      setIsButtonLoading(false);
      return;
    }
    const files = Object.values(listOfFiles).filter((f): f is { fileVersion: ObjectIdString } => !!f);

    if (files.length) {
      const fileVersionIds = files.map((file) => file.fileVersion);
      const response = await addFilesToEstimate({
        estimateId,
        fileVersionIds,
      });

      if (response.data.status) {
        setShowFileManager(false);
        filesGrid.current?.refresh?.();
      } else {
        const content =
          response.data.errorCode == errorCodes.ADDONS_NO_ACCESS
            ? t("addons_access_text_normal", { name: ADDONS_DISPLAY_NAME[BIM_TAKEOFF_ADDON_CODE] })
            : SOMETHING_WENT_WRONG;
        dispatch(showToastNotification({ content, type: TOAST_TYPE_ERROR }));
      }
    }
    setIsButtonLoading(false);
  };

  const fileVersionUpgradeClick = (params: FileRecord, e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    const isUpgradePossibe = isFileVersionUpgradePossible({
      resourceType: params.resourceType ?? "",
      sourceFileVersions: params.sourceFileVersions,
      cachingStatus: params.cachingStatus,
      conversionStatus: params.conversionStatus,
    });
    if (!isUpgradePossibe) {
      dispatch(
        showToastNotification({
          content: t("File Upgrade is only possible once conversion is done!"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return;
    }
    setUpgradeFile(params);
  };

  const verionUpgradeTemplate = (_data: unknown, _value: unknown, _a: unknown, _b: unknown, args: FileRecord) => {
    const container = document.createElement("div");
    container.classList.add("w-full");
    const root = createRoot(container);
    root.render(
      <div className="flex w-full items-center justify-between">
        <span>{` ${args.version}`}</span>
        {args.isFileUpdateRequired &&
          !args.fileDeleted &&
          !params.versionId &&
          isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id) && (
            <IconComponent
              name="Update"
              position="left"
              className="cursor-pointer"
              onClick={(e: React.MouseEvent) => fileVersionUpgradeClick(args, e)}
              toolTipContent={t("New version available")}
              width="1rem"
              height="1rem"
            />
          )}
      </div>,
    );
    return container;
  };

  const dateTemplate = (_data: unknown, _value: unknown, _a: unknown, _b: unknown, params: FileRecord) => {
    const dateFormat = convertDate(Number(params.addedDate), "dateForTooltip");
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      <div style={{ width: "fit-content" }}>
        <ToolTipComponent showTipPointer position="bottom" content={dateFormat}>
          <span>{convertDate(Number(params.addedDate), "dateForText")}</span>
        </ToolTipComponent>
      </div>,
    );

    return container;
  };

  const fileTemplate = (_data: unknown, _value: unknown, _a: unknown, _b: unknown, params: FileRecord) => {
    const container = document.createElement("div");
    container.classList.add("w-full");
    const root = createRoot(container);
    root.render(
      <div className="flex items-center justify-between w-full gap-1">
        <span className="text-ellipsis overflow-hidden whitespace-nowrap">{params.fileName}</span>
        {!!params.fileDeleted && (
          <IconComponent
            name="details"
            width="1.5rem"
            height="1.5rem"
            color="#FF0000"
            showGradient
            position="bottom"
            toolTipContent={t("This file appears to be missing.")}
          />
        )}
      </div>,
    );

    return container;
  };

  const addedByTemplate = (_data: unknown, _value: unknown, _a: unknown, _b: unknown, args: FileRecord) => {
    const container = document.createElement("div");
    renderUserTag(container, { id: args.addedBy?._id });
    return container;
  };

  const handleCloseFileUpgrade = () => {
    setUpgradeFile(null);
  };

  const handleCloseUpgrade = () => {
    setUpgradeFile(null);
    setIsButtonLoading(false);
    handleCloseResultsModal();
    filesGrid.current?.refresh?.();
  };

  const handleFileUpgrade = async () => {
    setIsButtonLoading(true);
    if (!upgradeFile) {
      setIsButtonLoading(false);
      return;
    }
    const data: UpgradeFileVersionRequest = {
      estimateId: params.estimateId!,
      fileVersionId: upgradeFile.id,
      ...(upgradeFile.wizardDetails?._id ? { reRunWizard: isUpgradeAndReRunWizard } : {}),
    };
    const response = await axiosWrapper.post<UpgradeFileVersionResponse>(
      `${getBackendUrl()}/estimate/upgradeFileVersionController`,
      data,
    );

    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("File version upgraded successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: t("Error Occured"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    handleCloseUpgrade();
  };

  const versionUpgradeConfirmClickHandler = async () => {
    if (_.get(upgradeFile, "wizardDetails._id") && isUpgradeAndReRunWizard) {
      setOpenResultsModal(true);
    } else {
      await handleFileUpgrade();
    }
  };

  const handleCloseResultsModal = () => {
    setOpenResultsModal(false);
  };

  const modalContent = () => (
    <div className="flex flex-col gap-6">
      <Text className="">{t("Are you sure you want to upgrade file version?")}</Text>
      {upgradeFile?.wizardDetails?._id && (
        <div className="flex flex-col gap-3">
          <RadioButton
            label={t("Just upgrade file version")}
            onClick={() => setIsUpgradeAndReRunWizard(false)}
            checked={!isUpgradeAndReRunWizard}
          />
          <RadioButton
            label={t("Upgrade and re-run the Model to Takeoff Wizard")}
            onClick={() => setIsUpgradeAndReRunWizard(true)}
            checked={isUpgradeAndReRunWizard}
          />
          <Text className="p-4 rounded-lg bg-banner_color_yellow bg-opacity-40" sx={{ whiteSpace: "normal" }}>
            {t(
              "Updating version of this file will result in deletion of all the Takeoffs and Measurements that were created using the Model to Takeoff Wizard.",
            )}
          </Text>
        </div>
      )}
    </div>
  );

  const versionUpgradeModal = () => (
    <ModalStateDialogComponent
      footerContent={
        <div className="flex gap-6">
          <ButtonComponent
            loading={isButtonLoading}
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            onClick={versionUpgradeConfirmClickHandler}
          >
            <Text>{t("Confirm")}</Text>
          </ButtonComponent>
          <ButtonComponent
            buttonType={BUTTON_TYPES.BORDER_BUTTON}
            onClick={handleCloseFileUpgrade}
            disabled={isButtonLoading}
          >
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      }
      onCancel={isButtonLoading ? undefined : handleCloseFileUpgrade}
      headerTitle={t("Upgrade Version")}
      isVisible={Boolean(upgradeFile)}
      modelContent={modalContent()}
      width="28.125rem"
    />
  );

  const resultsModalFooter = () => (
    <div className="flex gap-6">
      <ButtonComponent
        loading={isButtonLoading}
        disabled={!isResultLoadingDone}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={handleFileUpgrade}
      >
        {errorInAnyRecord ? "Proceed Anyway" : "Create Takeoffs"}
      </ButtonComponent>
      <ButtonComponent
        buttonType={BUTTON_TYPES.BORDER_BUTTON}
        onClick={handleCloseResultsModal}
        disabled={isButtonLoading}
      >
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  const resultsModalContent = () => {
    // `WizardResults` needs a real estimate id and all three wizard fields; the file row types them
    // optional, so the checked values are handed over rather than the partial object.
    const wizardDetails = upgradeFile?.wizardDetails;
    if (
      !upgradeFile ||
      !estimateId ||
      !wizardDetails?.quantityParameter ||
      !wizardDetails.codeField ||
      !wizardDetails.unitField
    ) {
      return undefined;
    }
    return (
      <WizardResults
        estimateId={estimateId}
        wizardDetails={{
          quantityParameter: wizardDetails.quantityParameter,
          codeField: wizardDetails.codeField,
          unitField: wizardDetails.unitField,
        }}
        setErrorInAnyRecord={setErrorInAnyRecord}
        setIsResultLoadingDone={setIsResultLoadingDone}
        isResultLoadingDone={isResultLoadingDone}
        targetFileVersionId={
          upgradeFile.resourceType == RESOURCE_TYPE_MERGED_FILE
            ? upgradeFile.sourceFileVersions?.map((file) => file.fileVersionId)
            : upgradeFile.targetFileVersionId
              ? [upgradeFile.targetFileVersionId]
              : undefined
        }
      />
    );
  };

  const resultsModal = () => (
    <ModalStateDialogComponent
      footerContent={resultsModalFooter()}
      onCancel={isButtonLoading ? undefined : handleCloseResultsModal}
      headerTitle={t("Run Wizard")}
      isVisible={openResultsModal}
      modelContent={resultsModalContent()}
      width="70%"
      minWidth="25rem"
    />
  );

  const filesGridColumns = useRef(
    typedColumns<FileRecord>([
      {
        field: "fileName",
        id: "fileName",
        name: t("Name"),
        textAlign: "Left",
        filterType: "Text",
        formatter: fileTemplate,
      },
      {
        field: "addedDate",
        id: "addedDate",
        name: t("Added On"),
        formatter: dateTemplate,
        textAlign: "Left",
        filterType: "Date",
      },
      {
        field: "addedBy",
        id: "addedBy",
        name: t("Added By"),
        textAlign: "Left",
        filterType: "Text",
        _valueGetter: (params: { addedBy?: { fullName?: string } }) => params.addedBy?.fullName,
        formatter: addedByTemplate,
      },
      {
        id: "type",
        field: "type",
        name: t("Type"),
        disableGrouping: true,
        disableFiltering: true,
        disableSorting: true,
        disableMenuOptions: true,
      },
      {
        id: "size",
        field: "size",
        name: t("Size"),
        disableGrouping: true,
        disableFiltering: true,
        disableSorting: true,
        disableMenuOptions: true,
      },
      {
        id: "version",
        field: "version",
        name: t("Version"),
        disableGrouping: true,
        disableFiltering: true,
        disableSorting: true,
        disableMenuOptions: true,
        formatter: verionUpgradeTemplate,
      },
    ]),
  );

  const getFilesGrid = () => (
    <div className="overflow-auto flex h-full">
      <SlickGrid1
        ref={filesGrid}
        columnDefinitions={filesGridColumns.current}
        rowHeight={60}
        enableSortingOption
        enableRowResize
        enableFilteringOption
        enableColumnChooser
        onSelectedRowIdsChanged={selectionEvent}
        remoteDataSource={estimateFilesUrl}
        infiniteScroll
        query={filesQuery}
        toolbar={toolbarOptions}
        toolbarClickHandler={toolbarClickHandler}
        onClick={recordClick as (e: GridClickEvent) => void}
      />
    </div>
  );

  return (
    <div className="h-[88%] overflow-y-auto">
      {addonsAccess.isReady && (
        <>
          {showFileManager && estimateData._id && (
            <FileManagerAsPopup
              isLoadingAddFiles={isButtonLoading}
              projectId={params.projectId}
              workspaceId={estimateData.space?._id ?? ""}
              id="fileManagerAsPopup"
              handleFileSelectedButtonClick={filesAdded}
              handleFileSelectedCancelClick={() => setShowFileManager(false)}
              hideIssues
              hideTasks
              hideSchedules
              hideEstimates
              disableFiles={filesGrid.current?.dataView.getItems().map((record) => record.id) ?? []}
            />
          )}
          {getFilesGrid()}
          {upgradeFile && versionUpgradeModal()}
          {openResultsModal && resultsModal()}
        </>
      )}
    </div>
  );
}

export default Files;
